/**
 * Datenmodell der Pflanzen-App.
 *
 * Alle gespeicherten Objekte werden mit diesen Schemas geprüft. Neue Felder
 * bekommen immer einen Standardwert (`.default(...)`), dadurch funktionieren
 * alte Datensätze ohne Datenbank-Migration weiter (siehe README → Erweitern).
 */
import { z } from 'zod';

export const API_VERSION = 1 as const;

export const IsoDate = z.iso.datetime();
export const DateKey = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
export const Id = z.string().min(1).max(64);
export const Slug = z.string().regex(/^[a-z0-9](?:[a-z0-9-]{0,38}[a-z0-9])?$/);
export const Hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);
export const TypeKey = z.string().regex(/^[a-z][a-z0-9_]{1,29}$/);
export const SpeciesKey = z.string().regex(/^[a-z0-9][a-z0-9-]{1,40}$/);

export const PotMaterial = z.enum(['plastic', 'terracotta', 'ceramic', 'self_watering', 'other']);
export type PotMaterial = z.infer<typeof PotMaterial>;

export const WaterType = z.enum(['tap', 'filtered', 'rain', 'distilled', 'mixed']);
export type WaterType = z.infer<typeof WaterType>;

/** Ergebnis des Fingertests (von knochentrocken bis nass). */
export const Soil = z.enum(['very_dry', 'dry', 'slightly_moist', 'moist']);
export type Soil = z.infer<typeof Soil>;

/**
 * Was bei einer Aufgabe passiert ist.
 * done        = erledigt (z. B. gegossen)
 * still_moist = geprüft, Erde noch feucht → nicht gegossen
 * skipped     = diesmal ausgelassen
 * snoozed     = „später erinnern“
 */
export const Outcome = z.enum(['done', 'still_moist', 'skipped', 'snoozed']);
export type Outcome = z.infer<typeof Outcome>;

export const EventSource = z.enum(['app', 'shortcut', 'nfc', 'siri', 'push', 'import', 'sitter']);
export type EventSource = z.infer<typeof EventSource>;

export const WindowState = z.enum(['closed', 'tilted', 'open']);
export type WindowState = z.infer<typeof WindowState>;

export const CurtainState = z.enum(['open', 'closed']);
export type CurtainState = z.infer<typeof CurtainState>;

/** Wo die Pflanzen stehen, wenn man im Raum steht und aufs Fenster schaut. */
export const PlantSide = z.enum(['left', 'center', 'right']);
export type PlantSide = z.infer<typeof PlantSide>;

/** Verhalten im Winter: Abstand strecken, pausieren (z. B. Dünger) oder gleich lassen. */
export const WinterMode = z.enum(['stretch', 'pause', 'none']);
export type WinterMode = z.infer<typeof WinterMode>;

export const FirstDue = z.enum(['now', 'after_interval']);
export type FirstDue = z.infer<typeof FirstDue>;

export const DirectSun = z.enum(['avoid', 'morning_ok', 'tolerant']);
export type DirectSun = z.infer<typeof DirectSun>;

export const Room = z.object({
  id: Id,
  name: z.string().min(1).max(40),
  /** Himmelsrichtung, in die das Fenster zeigt (0 = Nord, 90 = Ost, 180 = Süd, 270 = West); null = kein Fenster. */
  windowAzimuth: z.number().min(0).max(359).nullable().default(180),
  plantSide: PlantSide.default('center'),
  window: WindowState.default('closed'),
  curtain: CurtainState.default('open'),
  windowChangedAt: IsoDate.nullable().default(null),
  curtainChangedAt: IsoDate.nullable().default(null),
});
export type Room = z.infer<typeof Room>;

/** Pflanzen-Steckbrief. Eingebaute stehen in catalog.ts, eigene legt man in der App an. */
export const Species = z.object({
  key: SpeciesKey,
  name: z.string().min(1).max(60),
  aliases: z.array(z.string().max(60)).max(10).default([]),
  emoji: z.string().max(12).default('🪴'),
  /** Grundabstand fürs Prüfen/Gießen in der Wachstumszeit (Tage). */
  waterDays: z.number().min(1).max(120),
  /** Wie stark der Abstand an den kürzesten Tagen wächst (0,4 = +40 %, 1 = doppelt). */
  winterStretch: z.number().min(0).max(3).default(0.4),
  /** Idealer Zustand der Erde zum Gießen. */
  idealSoil: Soil.default('dry'),
  fingerDepthCm: z.number().min(1).max(15).default(2),
  humidityTarget: z.number().min(20).max(90).default(50),
  tempMinC: z.number().min(-10).max(30).default(15),
  tempMaxC: z.number().min(15).max(45).default(27),
  directSun: DirectSun.default('morning_ok'),
  draftSensitive: z.boolean().default(false),
  fluorideSensitive: z.boolean().default(false),
  toxicity: z.string().max(200).nullable().default(null),
  /** Aufgaben, die neue Pflanzen dieser Art automatisch bekommen: Typ → Abstand in Tagen. */
  tasks: z.record(TypeKey, z.number().min(0.5).max(730)).default({}),
  light: z.string().max(240).default(''),
  tips: z.array(z.string().max(320)).max(12).default([]),
  sourceUrl: z.string().max(300).nullable().default(null),
  builtin: z.boolean().default(false),
});
export type Species = z.infer<typeof Species>;

export const Plant = z.object({
  id: Id,
  name: z.string().min(1).max(60),
  /** Kurzname für Kurzbefehle/NFC, z. B. „calathea“. */
  slug: Slug,
  speciesKey: SpeciesKey.nullable().default(null),
  roomId: Id,
  emoji: z.string().max(12).default('🪴'),
  potMaterial: PotMaterial.default('plastic'),
  potDiameterCm: z.number().min(3).max(120).nullable().default(null),
  waterType: WaterType.default('tap'),
  hasSaucer: z.boolean().default(true),
  notes: z.string().max(2000).default(''),
  coverPhotoId: Id.nullable().default(null),
  createdAt: IsoDate,
  archivedAt: IsoDate.nullable().default(null),
});
export type Plant = z.infer<typeof Plant>;

export const TaskType = z.object({
  key: TypeKey,
  /** Name der Aufgabe, z. B. „Gießen“. */
  label: z.string().min(1).max(40),
  /** Was die Erinnerung sagt, z. B. „Erde prüfen“. */
  action: z.string().min(1).max(40),
  /** Text nach dem Erledigen, z. B. „Gegossen“. */
  doneLabel: z.string().min(1).max(40),
  icon: z.string().max(12).default('✅'),
  defaultIntervalDays: z.number().min(0.5).max(730),
  /** Vor dem Erledigen den Fingertest abfragen. */
  fingerTest: z.boolean().default(false),
  /** Abstand aus den Fingertests lernen. */
  learn: z.boolean().default(false),
  winter: WinterMode.default('none'),
  firstDue: FirstDue.default('after_interval'),
  points: z.number().int().min(0).max(50).default(5),
  /** Bei trockener Heizungsluft doppelt so oft fällig (für „Luftfeuchte“). */
  dryAirBoost: z.boolean().default(false),
  description: z.string().max(600).default(''),
  builtin: z.boolean().default(false),
});
export type TaskType = z.infer<typeof TaskType>;

export const CareTask = z.object({
  id: Id,
  plantId: Id,
  typeKey: TypeKey,
  /** Eigener Abstand in Tagen; null = Wert aus Steckbrief bzw. Aufgaben-Typ. */
  intervalDays: z.number().min(0.5).max(730).nullable().default(null),
  enabled: z.boolean().default(true),
  /** Manuell pausiert bis (z. B. Urlaub oder Krankheit). */
  pausedUntil: DateKey.nullable().default(null),
  createdAt: IsoDate,
});
export type CareTask = z.infer<typeof CareTask>;

export const CareEvent = z.object({
  id: Id,
  taskId: Id,
  plantId: Id,
  typeKey: TypeKey,
  outcome: Outcome,
  soil: Soil.nullable().default(null),
  at: IsoDate,
  /** An welchem Tag die Aufgabe fällig war (für die Prüf-Serie). */
  dueKey: DateKey.nullable().default(null),
  source: EventSource.default('app'),
  note: z.string().max(500).nullable().default(null),
  photoId: Id.nullable().default(null),
  snoozeUntil: IsoDate.nullable().default(null),
  deletedAt: IsoDate.nullable().default(null),
});
export type CareEvent = z.infer<typeof CareEvent>;

export const Photo = z.object({
  id: Id,
  plantId: Id,
  takenAt: IsoDate,
  width: z.number().int().min(1).max(20000),
  height: z.number().int().min(1).max(20000),
  eventId: Id.nullable().default(null),
  note: z.string().max(300).nullable().default(null),
});
export type Photo = z.infer<typeof Photo>;

export const NotifySettings = z.object({
  /** Gewitter, Sturm, Starkregen bei offenem Fenster. */
  weatherAlerts: z.boolean().default(true),
  /** Starke direkte Sonne auf empfindlichen Pflanzen bei offenem Vorhang. */
  sun: z.boolean().default(true),
  /** Kalte Zugluft bei offenem Fenster. */
  draft: z.boolean().default(true),
  /** Lüftungs-Tipps (z. B. milder Regen) auch als Mitteilung. */
  airingTips: z.boolean().default(false),
  /** Unwetter-Warnungen auch in der Ruhezeit. */
  alertsInQuiet: z.boolean().default(false),
});
export type NotifySettings = z.infer<typeof NotifySettings>;

export const Location = z.object({
  name: z.string().min(1).max(60).default('Wien'),
  lat: z.number().min(-90).max(90).default(48.2082),
  lon: z.number().min(-180).max(180).default(16.3738),
});
export type Location = z.infer<typeof Location>;

export const Settings = z.object({
  userName: z.string().max(40).default('Alex'),
  timezone: z.string().min(3).max(60).default('Europe/Vienna'),
  location: Location.prefault({}),
  /** Tägliche Erinnerung am Morgen. */
  morningTime: Hhmm.default('08:00'),
  /** Zweite Erinnerung, falls noch etwas offen ist (null = aus). */
  followUpTime: Hhmm.nullable().default('18:00'),
  quietStart: Hhmm.default('22:00'),
  quietEnd: Hhmm.default('07:00'),
  /** Minuten nach dem Gießen bis „Untersetzer leeren“ (0 = aus). */
  saucerMinutes: z.number().int().min(0).max(360).default(60),
  indoorTempC: z.number().min(10).max(35).default(21),
  notify: NotifySettings.prefault({}),
  /** Erhöhen macht alte Kurzbefehl- und Kalender-Links ungültig. */
  linkVersion: z.number().int().min(1).max(10_000).default(1),
});
export type Settings = z.infer<typeof Settings>;

export const AppState = z.object({
  apiVersion: z.literal(API_VERSION),
  serverTime: IsoDate,
  settings: Settings,
  rooms: z.array(Room),
  plants: z.array(Plant),
  taskTypes: z.array(TaskType),
  tasks: z.array(CareTask),
  events: z.array(CareEvent),
  photos: z.array(Photo),
  /** Nur selbst angelegte Steckbriefe; eingebaute kommen aus catalog.ts. */
  species: z.array(Species),
});
export type AppState = z.infer<typeof AppState>;

/** Macht aus einem Namen einen Kurznamen: „Calathea Ornata“ → „calathea-ornata“. */
export function slugify(name: string): string {
  const map: Record<string, string> = { ä: 'ae', ö: 'oe', ü: 'ue', ß: 'ss' };
  const s = name
    .toLowerCase()
    .replace(/[äöüß]/g, (c) => map[c] ?? c)
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40)
    .replace(/-+$/g, '');
  return s || 'pflanze';
}

export const SOIL_RANK: Record<Soil, number> = { very_dry: 0, dry: 1, slightly_moist: 2, moist: 3 };

export const SOIL_LABEL: Record<Soil, string> = {
  very_dry: 'Knochentrocken',
  dry: 'Trocken',
  slightly_moist: 'Leicht feucht',
  moist: 'Nass',
};

/** Soll bei diesem Fingertest-Ergebnis gegossen werden? */
export function shouldWater(soil: Soil, ideal: Soil): boolean {
  return SOIL_RANK[soil] <= SOIL_RANK[ideal];
}
